import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  MapPin, 
  Clock, 
  PlayCircle,
  PauseCircle,
  Coffee,
  StopCircle,
  AlertTriangle,
  Calendar,
  Loader2,
  Timer,
  Banknote
} from 'lucide-react';
import { toast } from 'sonner';
import { useIsLargeScreen } from '../../hooks/useIsLargeScreen';

export default function ShiftTrackerPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  // Only emit the data-walkthrough attr on mobile — desktop already has the
  // anchor on the sidebar nav link via WorkerLayout.
  const isLargeScreen = useIsLargeScreen();
  const [shifts, setShifts] = useState([]);
  const [activeShift, setActiveShift] = useState(null);
  const [timeLog, setTimeLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [nextCheckDue, setNextCheckDue] = useState(null);
  const [showTillStartDialog, setShowTillStartDialog] = useState(false);
  const [startTillAmount, setStartTillAmount] = useState('');
  const [tillStartRecorded, setTillStartRecorded] = useState(false);

    const fetchMyShifts = useCallback(async () => {
    try {
      const response = await api.get('/shifts/my-shifts');
      setShifts(response.data);
      
      // Find active or assigned shift
      const active = response.data.find(s => s.status === 'in_progress');
      const assigned = response.data.find(s => s.status === 'assigned');
      
      if (active) {
        setActiveShift(active);
        setTimeLog(active.timeLog);
        calculateNextCheck(active, active.timeLog);
        // Check if starting till was recorded
        try {
          const auditRes = await api.get(`/till-audit/shift/${active.id}`);
          const hasStart = auditRes.data.logs?.some(l => l.logType === 'start');
          setTillStartRecorded(hasStart);
        } catch {
          setTillStartRecorded(false);
        }
      } else if (assigned) {
        setActiveShift(assigned);
        setTimeLog(null);
        setTillStartRecorded(false);
      } else {
        // No active/assigned shift — clear state
        setActiveShift(null);
        setTimeLog(null);
        setTillStartRecorded(false);
      }
    } catch (error) {
      toast.error('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchMyShifts();
  }, [fetchMyShifts]);

  // Timer for elapsed time
  useEffect(() => {
    if (!timeLog?.actualStartTime || timeLog?.actualEndTime) return;
    
    const start = new Date(timeLog.actualStartTime);
    const updateElapsed = () => {
      const now = new Date();
      let elapsed = Math.floor((now - start) / 1000 / 60); // minutes
      
      // Subtract break time if on break
      if (timeLog.breakStartTime && !timeLog.breakEndTime) {
        const breakStart = new Date(timeLog.breakStartTime);
        elapsed -= Math.floor((now - breakStart) / 1000 / 60);
      } else if (timeLog.breakStartTime && timeLog.breakEndTime) {
        const breakStart = new Date(timeLog.breakStartTime);
        const breakEnd = new Date(timeLog.breakEndTime);
        elapsed -= Math.floor((breakEnd - breakStart) / 1000 / 60);
      }
      
      setElapsedTime(Math.max(0, elapsed));
    };
    
    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [timeLog]);

  const calculateNextCheck = (shift, log) => {
    if (!log?.actualStartTime || log?.actualEndTime) {
      setNextCheckDue(null);
      return;
    }
    
    // Calculate shift length
    const start = shift.scheduledStartTime.split(':');
    const end = shift.scheduledEndTime.split(':');
    const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
    const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
    const shiftLength = endMinutes - startMinutes;
    
    // If shift is 4 hours or less, no 3-hour checks needed
    if (shiftLength <= 240) {
      setNextCheckDue(null);
      return;
    }
    
    // Calculate when next check is due (every 3 hours from start)
    const actualStart = new Date(log.actualStartTime);
    const now = new Date();
    const elapsedHours = (now - actualStart) / 1000 / 60 / 60;
    const checksCompleted = Math.floor(elapsedHours / 3);
    const nextCheckHours = (checksCompleted + 1) * 3;
    const nextCheckTime = new Date(actualStart.getTime() + nextCheckHours * 60 * 60 * 1000);
    
    if (nextCheckTime > now) {
      setNextCheckDue(nextCheckTime);
    } else {
      // Check is overdue
      setNextCheckDue(new Date()); // Due now
    }
  };

  const handleAction = async (action) => {
    if (!activeShift) return;
    
    // If starting shift, show till start dialog first
    if (action === 'start_shift') {
      setShowTillStartDialog(true);
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await api.post(`/shifts/${activeShift.id}/time-log`, { action });
      setTimeLog(response.data);
      
      if (action === 'start_break') {
        toast.success('Break started');
      } else if (action === 'end_break') {
        toast.success('Break ended');
      } else if (action === 'end_shift') {
        if (response.data.needsFinalTill) {
          toast.success('Shift ended! Please submit your final till amount.');
          navigate('/worker/till-checks', { state: { shiftId: activeShift.id, submitTillLog: true } });
        } else {
          toast.success('Shift completed successfully!');
          setActiveShift(null);
          setTimeLog(null);
          navigate('/worker/my-shifts');
        }
      }
      
      fetchMyShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartShiftWithTill = async () => {
    if (!activeShift || !startTillAmount) return;
    setActionLoading(true);
    try {
      // Start the shift first
      const response = await api.post(`/shifts/${activeShift.id}/time-log`, { action: 'start_shift' });
      setTimeLog(response.data);
      
      // Record starting till amount
      await api.post('/till-audit/start', {
        shiftId: activeShift.id,
        actualAmount: parseFloat(startTillAmount)
      });
      
      setTillStartRecorded(true);
      setShowTillStartDialog(false);
      setStartTillAmount('');
      toast.success('Shift started! Starting till recorded.');
      calculateNextCheck(activeShift, response.data);
      fetchMyShifts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start shift');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTimeOfDay = (date) => {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const isOnBreak = timeLog?.breakStartTime && !timeLog?.breakEndTime;
  const isCheckDue = nextCheckDue && nextCheckDue <= new Date();

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (!activeShift) {
    return (
      <div className="p-6 space-y-6" data-testid="tracker-no-shift">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={!isLargeScreen ? 'wt-worker-active' : undefined} data-testid="todays-shift-heading">Today&apos;s Shift</h1>
          <p className="text-slate-500 mt-1">Track your active shift</p>
        </div>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <Clock className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No Active Shift</h3>
            <p className="text-slate-500 mb-6">You don't have an assigned or active shift right now</p>
            <Button 
              className="rounded-full bg-[#DA291C] hover:bg-[#b91f13]"
              onClick={() => navigate('/worker')}
            >
              Browse Available Shifts
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="shift-tracker-page">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Public_Sans']" data-walkthrough={!isLargeScreen ? 'wt-worker-active' : undefined} data-testid="todays-shift-heading">Today&apos;s Shift</h1>
        <p className="text-slate-500 mt-1 text-base">Follow the steps — we&apos;ll only show what you need next.</p>
      </div>

      {/* Shift Info Card */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900 font-['Public_Sans']">{activeShift.branchName}</h2>
              <div className="flex items-center gap-1 text-slate-500 mt-1">
                <MapPin className="h-4 w-4" />
                <span>{activeShift.location}</span>
              </div>
            </div>
            <Badge className={`${
              activeShift.status === 'in_progress' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
            } font-medium`}>
              {activeShift.status === 'in_progress' ? 'Active' : 'Assigned'}
            </Badge>
          </div>
          
          <div className="grid grid-cols-2 gap-4 py-3 px-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Date</p>
                <p className="text-sm font-medium text-slate-900">{activeShift.date}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500">Scheduled</p>
                <p className="text-sm font-medium text-slate-900">{activeShift.scheduledStartTime} - {activeShift.scheduledEndTime}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timer Display */}
      {timeLog?.actualStartTime && !timeLog?.actualEndTime && (
        <Card className={`border-0 shadow-sm ${isOnBreak ? 'bg-amber-50' : 'bg-emerald-50'}`}>
          <CardContent className="p-6 text-center">
            <p className="text-sm font-medium text-slate-600 mb-2">
              {isOnBreak ? 'On Break' : 'Time Worked'}
            </p>
            <div className={`text-5xl font-bold tabular-nums ${isOnBreak ? 'text-amber-600' : 'text-emerald-600'}`}>
              {formatTime(elapsedTime)}
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Started at {formatTimeOfDay(new Date(timeLog.actualStartTime))}
            </p>
          </CardContent>
        </Card>
      )}

      {/* 3-Hour Check Alert */}
      {isCheckDue && (
        <Card className="border-0 shadow-sm bg-red-50 ring-2 ring-red-200">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="bg-red-100 p-3 rounded-xl">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-red-800">3-Hour Till Check Due</h3>
                <p className="text-sm text-red-600">Please complete your till discrepancy check now</p>
              </div>
            </div>
            <Button
              className="w-full mt-4 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold"
              onClick={() => navigate('/worker/till-checks', { state: { shiftId: activeShift.id } })}
              data-testid="till-check-btn"
            >
              Complete Till Check
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Next Check Time */}
      {nextCheckDue && !isCheckDue && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <Timer className="h-5 w-5 text-slate-400" />
            <div>
              <p className="text-sm text-slate-500">Next till check due</p>
              <p className="font-medium text-slate-900">{formatTimeOfDay(nextCheckDue)}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Log Details */}
      {timeLog && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold font-['Public_Sans']">Time Log</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <span className="text-slate-500">Started</span>
                <span className="font-medium text-slate-900">
                  {timeLog.actualStartTime ? formatTimeOfDay(new Date(timeLog.actualStartTime)) : '-'}
                </span>
              </div>
              {timeLog.breakStartTime && (
                <div className="flex items-center justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-500">Break</span>
                  <span className="font-medium text-slate-900">
                    {formatTimeOfDay(new Date(timeLog.breakStartTime))}
                    {timeLog.breakEndTime && ` - ${formatTimeOfDay(new Date(timeLog.breakEndTime))}`}
                  </span>
                </div>
              )}
              {timeLog.actualEndTime && (
                <div className="flex items-center justify-between py-2">
                  <span className="text-slate-500">Ended</span>
                  <span className="font-medium text-slate-900">
                    {formatTimeOfDay(new Date(timeLog.actualEndTime))}
                  </span>
                </div>
              )}
            </div>
            
            {/* Status Badges — computed independently on the backend.
                A worker who started late but stayed to the scheduled end will
                see ONLY the "Started late" badge, not "Left early". */}
            <div className="flex gap-2 mt-4">
              {timeLog.lateFlag && (
                <Badge className="bg-amber-100 text-amber-700" data-testid="badge-started-late">Started late</Badge>
              )}
              {timeLog.leftEarlyFlag && (
                <Badge className="bg-amber-100 text-amber-700" data-testid="badge-left-early">Left early</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="space-y-3">
        {!timeLog?.actualStartTime && (
          <Button
            className="w-full h-14 rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white font-semibold text-lg"
            onClick={() => handleAction('start_shift')}
            disabled={actionLoading}
            data-testid="start-shift-btn"
          >
            {actionLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : (
              <PlayCircle className="h-5 w-5 mr-2" />
            )}
            Start Shift
          </Button>
        )}

        {timeLog?.actualStartTime && !timeLog?.actualEndTime && (
          <>
            {!isOnBreak ? (
              <Button
                className="w-full h-14 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-semibold text-lg"
                onClick={() => handleAction('start_break')}
                disabled={actionLoading}
                data-testid="start-break-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <Coffee className="h-5 w-5 mr-2" />
                )}
                Start Break
              </Button>
            ) : (
              <Button
                className="w-full h-14 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-lg"
                onClick={() => handleAction('end_break')}
                disabled={actionLoading}
                data-testid="end-break-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                ) : (
                  <PlayCircle className="h-5 w-5 mr-2" />
                )}
                End Break
              </Button>
            )}

            <Button
              className="w-full h-14 rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white font-semibold text-lg"
              onClick={() => handleAction('end_shift')}
              disabled={actionLoading || isOnBreak}
              data-testid="end-shift-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
              ) : (
                <StopCircle className="h-5 w-5 mr-2" />
              )}
              End Shift
            </Button>
          </>
        )}
      </div>

      {/* Starting Till Dialog */}
      <Dialog open={showTillStartDialog} onOpenChange={(open) => { if (!open) setShowTillStartDialog(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-['Public_Sans']">
              <Banknote className="h-5 w-5 text-[#DA291C]" />
              Enter Starting Till Amount
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-500">Record the current till amount before starting your shift. This becomes the baseline for all till checks.</p>
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Till Amount (&pound;)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={startTillAmount}
                onChange={(e) => setStartTillAmount(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && startTillAmount && handleStartShiftWithTill()}
                className="h-14 rounded-xl bg-slate-50 border-slate-200 text-2xl font-bold text-center"
                autoFocus
                data-testid="start-till-amount-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setShowTillStartDialog(false)}>Cancel</Button>
            <Button
              className="rounded-full bg-[#DA291C] hover:bg-[#b91f13] text-white"
              onClick={handleStartShiftWithTill}
              disabled={actionLoading || !startTillAmount}
              data-testid="confirm-start-till-btn"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              Start Shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
